
import { useState, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ContentType, useContentCalendar } from "@/hooks/useContentCalendar";
import { startOfMonth, endOfMonth, addDays, addMonths, subMonths, format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { ContentCalendarView } from "@/components/content-calendar/ContentCalendarView";
import { Button } from "@/components/ui/button";
import { 
  ChevronLeft, 
  ChevronRight, 
  Filter, 
  Plus, 
  CalendarDays
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { ViewStateManager } from "@/state/view/ViewStateManager";

const ContentCalendar = () => {
  useDocumentTitle("Content Calendar");
  const navigate = useNavigate();
  const viewStateManager = ViewStateManager.getInstance();
  
  // Load saved state from ViewStateManager if available
  const savedState = viewStateManager.getViewState("/dashboard/calendar") || {};
  
  // State for content filters and date navigation
  const [contentType, setContentType] = useState<ContentType>(savedState.contentType || "all");
  const [currentDate, setCurrentDate] = useState<Date>(savedState.currentDate ? new Date(savedState.currentDate) : new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(savedState.filters || {
    status: "all",
    category: "all"
  });
  const [viewMode, setViewMode] = useState<"list" | "month" | "week" | "day">(savedState.viewMode || "list");
  
  // Save state changes to ViewStateManager
  useEffect(() => {
    viewStateManager.updateViewState("/dashboard/calendar", {
      contentType,
      currentDate: currentDate.toISOString(),
      filters,
      viewMode
    });
  }, [contentType, currentDate, filters, viewMode]);
  
  // Navigation handlers
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());
  
  // Set date range to the current month plus 15 days before and after
  const startDate = addDays(startOfMonth(currentDate), -15);
  const endDate = addDays(endOfMonth(currentDate), 15);
  
  const { data, isLoading, error } = useContentCalendar({ 
    startDate, 
    endDate, 
    contentType,
    filters
  });
  
  // Handler for creating new content
  const handleNewContent = useCallback(() => {
    if (contentType === 'blog' || contentType === 'all') {
      navigate('/dashboard/blog/posts/new');
    } else if (contentType === 'linkedin') {
      navigate('/dashboard/linkedin/posts/new');
    }
  }, [contentType, navigate]);
  
  // Handler for filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // Reset filters handler
  const resetFilters = () => {
    setFilters({
      status: "all",
      category: "all"
    });
    setShowFilters(false);
  };

  // Handle view mode change
  const handleViewModeChange = (mode: "list" | "month" | "week" | "day") => {
    setViewMode(mode);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Content Calendar</h1>
          <p className="text-muted-foreground">
            Manage all your scheduled content in one place
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
          
          <div className="flex">
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-r-none px-2"
              onClick={goToPreviousMonth}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="rounded-l-none px-2"
              onClick={goToNextMonth}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowFilters(true)}
          >
            <Filter className="h-4 w-4 mr-1" />
            Filter
          </Button>
          
          <Button size="sm" onClick={handleNewContent}>
            <Plus className="h-4 w-4 mr-1" />
            Create Content
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              {format(currentDate, "MMMM yyyy")}
            </CardTitle>
            
            <Tabs
              value={contentType}
              onValueChange={(value) => setContentType(value as ContentType)}
              className="w-full sm:w-auto"
            >
              <TabsList>
                <TabsTrigger value="all">
                  All Content {data && `(${data.counts.total})`}
                </TabsTrigger>
                <TabsTrigger value="blog">
                  Blog {data && `(${data.counts.blog})`}
                </TabsTrigger>
                <TabsTrigger value="linkedin">
                  LinkedIn {data && `(${data.counts.linkedin})`}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-64 w-full" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center p-12 text-destructive">
              Error loading calendar data. Please try again.
            </div>
          ) : data ? (
            <ContentCalendarView 
              items={data.items} 
              currentDate={currentDate}
              onFilter={() => setShowFilters(true)}
              onNewContent={handleNewContent}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
            />
          ) : (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              No content found for the selected period
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Filter Sheet */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Content Filters</SheetTitle>
          </SheetHeader>
          
          <div className="py-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="status-filter">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange("status", value)}
              >
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category-filter">Category</Label>
              <Select
                value={filters.category}
                onValueChange={(value) => handleFilterChange("category", value)}
              >
                <SelectTrigger id="category-filter">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Development">Development</SelectItem>
                  <SelectItem value="Design">Design</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <SheetFooter>
            <Button variant="outline" onClick={resetFilters}>
              Reset Filters
            </Button>
            <Button onClick={() => setShowFilters(false)}>
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ContentCalendar;
